import { useState } from 'react'
import moment from 'moment'

import { CustomFieldType } from 'models/enums'
import CustomField from 'modules/shared/custom-fields/CustomField'
import DefaultModal from './DefaultModal'
import { dateFormat } from 'modules/referrals/referral-form/ReferralForm'
import CustomDatepicker from 'modules/shared/custom-fields/CustomInlineDatepicker'
import { useAppDispatch } from 'util/useAppDispatch'
import { formatDobForApi } from 'modules/shared/dateUtils'
import { loadEmailOrPhoneData, loadSwapTransactionData, saveNewEmailOrPhone, saveSwapTransaction } from './actions'
import CustomMultiselectField from 'modules/shared/custom-fields/CustomMultiselectField'

import './AdminToolsShared.sass'

interface Props {
    onClose: () => void
    practiceId: string
}

const emailRegex = /.+\@.+\..+/

const UpdateEmailModal = (props: Props) => {
    const dispatch = useAppDispatch()

    const [screenIndex, setScreenIndex] = useState(0)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [currentEmail, setTheCurrentEmail] = useState('')
    const [currentPhone, setTheCurrentPhone] = useState('')
    const [isLoadingEmailData, setIsLoadingEmailData] = useState(false)
    const [loadedData, setLoadedData] = useState<{
        practiceId: string
        portalUserId: string
        payerId: string
        firstName: string
        lastName: string
        email: string
        phone: string
    }>({
        practiceId: '',
        portalUserId: '',
        payerId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    })
    const [newEmail, setTheNewEmail] = useState('')
    const [isSavingEmail, setIsSavingEmail] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    const goBackScreen = () => {
        setErrorMessage('')
        setScreenIndex(currentScreenIndex => currentScreenIndex - 1)
    }

    const goForwardScreen = () => {
        setErrorMessage('')
        setScreenIndex(currentScreenIndex => currentScreenIndex + 1)
    }

    const onLoadEmailData = async () => {
        if (isLoadingEmailData) {
            return
        }

        setIsLoadingEmailData(true)
        try {
            const emailData = await dispatch(
                loadEmailOrPhoneData(
                    props.practiceId,
                    firstName,
                    lastName,
                    currentEmail || undefined,
                    currentPhone || undefined,
                ),
            )
            setLoadedData({
                practiceId: emailData[0].practiceId,
                portalUserId: emailData[0].portalUserId,
                payerId: emailData[0].payerId,
                firstName: emailData[0].firstName,
                lastName: emailData[0].lastName,
                email: currentEmail,
                phone: currentPhone,
            })
            goForwardScreen()
        } catch (err) {
            if (err.errors && !!err.errors[0] && err.errors[0].message) {
                setErrorMessage(err.errors[0].message)
            } else {
                setErrorMessage('Server error.')
            }
        }
        setIsLoadingEmailData(false)
    }

    const onSaveEmail = async () => {
        if (isSavingEmail) {
            return
        }
        setIsSavingEmail(true)
        try {
            const emailData = await dispatch(saveNewEmailOrPhone(loadedData.portalUserId, loadedData.payerId, newEmail))
            if (emailData === true) {
                goForwardScreen()
                setSuccessMessage(`Email address successfully updated to ${newEmail}.`)
            }
        } catch (err) {
            if (err.errors && !!err.errors[0] && err.errors[0].message) {
                setErrorMessage(err.errors[0].message)
            } else {
                setErrorMessage('Server error.')
            }
        }
        setIsSavingEmail(false)
    }

    return (
        <DefaultModal
            title="Update Email Address"
            onClose={props.onClose}
            screenIndex={screenIndex}
            successMessage={successMessage}
            errorMessage={errorMessage}
            screens={[
                {
                    content: (
                        <div className="admin-tools-content admin-tools-content--input">
                            <div className="admin-tools-row">
                                <CustomField
                                    customFieldType={CustomFieldType.INPUT}
                                    autoFocus={false}
                                    label="First Name"
                                    // inputProps={{ step: '.01' }}
                                    inputType="text"
                                    value={firstName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                />
                                <CustomField
                                    customFieldType={CustomFieldType.INPUT}
                                    autoFocus={false}
                                    label="Last Name"
                                    // inputProps={{ step: '.01' }}
                                    inputType="text"
                                    value={lastName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                />
                            </div>
                            <div className="admin-tools-row">
                                <CustomField
                                    customFieldType={CustomFieldType.INPUT}
                                    autoFocus={false}
                                    label="Current Email"
                                    // inputProps={{ step: '.01' }}
                                    inputType="text"
                                    value={currentEmail}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setTheCurrentEmail(e.target.value)
                                    }
                                />
                                <span className="admin-tools-or-text">Or</span>
                                <CustomField
                                    customFieldType={CustomFieldType.INPUT}
                                    autoFocus={false}
                                    label="Current Phone #"
                                    // inputProps={{ step: '.01' }}
                                    inputType="text"
                                    value={currentPhone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setTheCurrentPhone(e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    ),
                    mainButton: {
                        isLoading: isLoadingEmailData,
                        disabled: firstName === '' || lastName === '' || (currentEmail === '' && currentPhone === ''),
                        onClick: onLoadEmailData,
                        text: 'Next',
                    },
                },
                {
                    content: (
                        <div className="admin-tools-content admin-tools-content--loaded">
                            <div className="admin-tools-details">
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">Practice ID:</div>
                                    <div className="admin-tools-details-value">{loadedData.practiceId}</div>
                                </div>
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">First Name:</div>
                                    <div className="admin-tools-details-value">{loadedData.firstName}</div>
                                </div>
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">Last Name:</div>
                                    <div className="admin-tools-details-value">{loadedData.lastName}</div>
                                </div>
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">Current Email:</div>
                                    <div className="admin-tools-details-value">{loadedData.email}</div>
                                </div>
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">Current Phone:</div>
                                    <div className="admin-tools-details-value">{loadedData.phone}</div>
                                </div>
                            </div>
                            <div className="admin-tools-select-wrapper">
                                <CustomField
                                    customFieldType={CustomFieldType.INPUT}
                                    autoFocus={false}
                                    label="Change email to:"
                                    // inputProps={{ step: '.01' }}
                                    inputType="text"
                                    value={newEmail}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setTheNewEmail(e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    ),
                    leftButton: {
                        onClick: goBackScreen,
                        text: 'Back',
                    },
                    mainButton: {
                        isLoading: isSavingEmail,
                        disabled: newEmail === '' || !emailRegex.test(newEmail),
                        onClick: goForwardScreen,
                        text: 'Review Changes',
                    },
                },
                {
                    content: (
                        <div className="admin-tools-content admin-tools-content--loaded">
                            <p className="admin-tools-text">Please confirm the following email address update:</p>
                            <div className="admin-tools-details">
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">Current Email:</div>
                                    <div className="admin-tools-details-value">{loadedData.email}</div>
                                </div>
                                <div className="admin-tools-details-row">
                                    <div className="admin-tools-details-label">New Email:</div>
                                    <div className="admin-tools-details-value">{newEmail}</div>
                                </div>
                            </div>
                        </div>
                    ),
                    leftButton: {
                        onClick: goBackScreen,
                        text: 'Back',
                    },
                    mainButton: {
                        isLoading: isSavingEmail,
                        onClick: onSaveEmail,
                        text: 'Submit',
                    },
                },
                {
                    content: <div className="admin-tools-content admin-tools-content--loaded"></div>,
                    mainButton: {
                        isLoading: false,
                        disabled: false,
                        onClick: props.onClose,
                        text: 'Close',
                    },
                },
            ]}
        />
    )
}

export default UpdateEmailModal
